import { getRecoil, setRecoil } from 'recoil-nexus';

import * as profile from '@/api/profile.service';
import { getEcho } from '@/realtime/echo-client';

import {
  boardState,
  turnState,
  gamePhaseState,
  winnerIdState,
  endgameReasonState,
  winningComboState,
  endgameScoreChangeState,
  resetBoardRematch,
  historyState,
  lastCellSelector,
} from '@/framework/board';
import { userState, userSelector } from '@/framework/user';
import { GamePhaseType, getGame, gameIdState, gameTypeState } from '@/framework/game';
import type { EndgameReason } from '@/framework/game/types';
import { userPlayerState, opponentPlayerState } from '@/framework/player';
import { normalizeGemType } from '@/framework/gem';
import { GemType } from '@/framework/gem/types';

import { pidState } from '@/use/usePidState';
import { openModal, closeModal } from '@/use/useModal';

type WirePlayer = {
  id: string;
  seat?: number;
  user_id?: number | string | null;
  name?: string;
  colour?: GemType | string;
  connected?: boolean | string;
  rematch?: boolean | string;
  rejoin?: number | null;
};

let subscribedToken: string | null = null;
let latestPlayers: WirePlayer[] = [];

function seatToPid(seat: number): string | undefined {
  return latestPlayers.find((p) => p.seat === seat)?.id;
}

function normalizeBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true' || v === '1') return true;
  if (v === 'false' || v === '0') return false;
  return Boolean(v);
}

function normalizeReason(v: unknown): EndgameReason | undefined {
  if (v === 'won' || v === 'resign' || v === 'abandon' || v === 'tie') {
    return v;
  }
  if (v === 'win') return 'won';
  return undefined;
}

function updatePlayersList(players: unknown) {
  if (!Array.isArray(players)) return;
  latestPlayers = players as WirePlayer[];
}

async function applyPlayerRecord(colyPlayer: WirePlayer) {
  const pid = getRecoil(pidState);
  if (!pid) return;

  const {
    colour,
    connected,
    user_id: userIdRaw,
    rematch,
    name,
    id,
    rejoin,
  } = colyPlayer;

  const userIdStr =
    userIdRaw === null || userIdRaw === undefined || userIdRaw === ''
      ? 'anon'
      : String(userIdRaw);

  const playerState = id === pid ? userPlayerState : opponentPlayerState;
  const isSelf = id === pid;

  setRecoil(playerState.connected, normalizeBool(connected));
  setRecoil(playerState.userId, userIdStr);
  setRecoil(playerState.rematch, normalizeBool(rematch));
  setRecoil(playerState.name, name ?? '');
  setRecoil(playerState.id, id);
  if (rejoin != null && typeof rejoin === 'number') {
    const mil = rejoin - Date.now();
    const seconds = Math.floor(mil / 1000);
    setRecoil(playerState.rejoin, seconds <= 0 ? undefined : seconds);
  } else {
    setRecoil(playerState.rejoin, undefined);
  }

  const fallback: GemType = isSelf
    ? (getRecoil(userState.activeGem) ?? 'red')
    : 'blue';
  setRecoil(playerState.colour, normalizeGemType(colour, fallback));

  if (userIdStr === 'anon') {
    setRecoil(playerState.name, name ?? 'Anonymous');
  } else if (isSelf && getRecoil(userSelector.loggedIn)) {
    setRecoil(playerState.name, getRecoil(userState.username));
    setRecoil(playerState.score, getRecoil(userState.score));
    setRecoil(playerState.country, getRecoil(userState.country));
    setRecoil(playerState.rank, getRecoil(userState.rank));
    const equipped = getRecoil(userState.activeGem);
    if (equipped) {
      setRecoil(playerState.colour, equipped);
    }
  } else {
    try {
      const { data } = await profile.getUserById(userIdStr);

      setRecoil(playerState.name, data.username);
      setRecoil(playerState.score, data.score);
      setRecoil(playerState.country, data.country);
      setRecoil(playerState.rank, data.rank);
      if (data.activeGem) {
        setRecoil(playerState.colour, data.activeGem);
      }
    } catch (err) {
      setRecoil(playerState.name, name ?? 'Anonymous');
    }
  }
}

function syncPlayersFromPayload(payload: Record<string, unknown>) {
  const players = payload.players as WirePlayer[] | undefined;
  if (Array.isArray(players) && players.length) {
    updatePlayersList(players);
    players.forEach((p) => {
      void applyPlayerRecord(p);
    });
  } else if (payload.player) {
    void applyPlayerRecord(payload.player as WirePlayer);
  }
}

function applyMoveFromPayload(payload: Record<string, unknown>) {
  const move = payload.move as
    | {
        seat?: number;
        col?: number;
        row?: number;
        player?: string;
      }
    | undefined;

  if (!move) return;

  const col = Number(move.col);
  const row = Number(move.row);
  if (Number.isNaN(col) || Number.isNaN(row)) return;

  const history = getRecoil(historyState) || [];
  if (history.some((h) => h.col === col && h.row === row)) {
    return;
  }

  const movePid =
    move.player ??
    (move.seat != null ? seatToPid(Number(move.seat)) : undefined) ??
    '';

  setRecoil(lastCellSelector, (lastCell) =>
    lastCell ? { ...lastCell, isLast: false } : undefined,
  );

  setRecoil(boardState[col][row], {
    pid: movePid,
    isLast: true,
  });

  setRecoil(historyState, (prev) => [...(prev || []), { col, row }]);
}

function applyTurnFromPayload(payload: Record<string, unknown>) {
  const rawTurn = payload.turn;
  if (rawTurn == null || rawTurn === '') return;

  if (typeof rawTurn === 'string' && Number.isNaN(Number(rawTurn))) {
    setRecoil(turnState, rawTurn);
    return;
  }

  const turnSeat = Number(rawTurn);
  if (!Number.isFinite(turnSeat)) return;

  const fromPayload = Array.isArray(payload.players)
    ? (payload.players as WirePlayer[]).find(
        (p) => Number(p.seat) === turnSeat,
      )?.id
    : undefined;
  const pidTurn = fromPayload ?? seatToPid(turnSeat);
  if (pidTurn) {
    setRecoil(turnState, pidTurn);
  }
}

function hydrateBoardFromMoves(payload: Record<string, unknown>) {
  const moves = payload.moves as
    | Array<{ seat?: number; col?: number; row?: number; player?: string }>
    | undefined;
  if (!Array.isArray(moves) || moves.length === 0) return;

  const history = getRecoil(historyState) || [];
  if (history.length >= moves.length) return;

  moves.forEach((move) => {
    applyMoveFromPayload({ move });
  });
}

async function handlePhaseOver() {
  openModal('endgame', {
    extra: { animate: true },
  });

  const gameId = getRecoil(gameIdState);
  if (gameId) {
    try {
      const { type } = await getGame(gameId);
      setRecoil(gameTypeState, type);
    } catch (err) {}
  }
}

export function hydrateFromSnapshot(payload: Record<string, unknown>) {
  updatePlayersList(payload.players);
  syncPlayersFromPayload(payload);

  const phase = payload.phase as GamePhaseType | undefined;
  if (phase) {
    setRecoil(gamePhaseState, phase);
  }
  applyTurnFromPayload(payload);
  hydrateBoardFromMoves(payload);
}

export function applyMoveSnapshot(payload: Record<string, unknown>) {
  updatePlayersList(payload.players);
  syncPlayersFromPayload(payload);
  applyMoveFromPayload(payload);
  applyTurnFromPayload(payload);
  if (payload.phase === 'over' || payload.reason) {
    handleGameFinished(payload);
  }
}

function handleGameFinished(payload: Record<string, unknown>) {
  const phase = payload.phase as GamePhaseType | undefined;
  if (phase) {
    setRecoil(gamePhaseState, phase);
  } else {
    setRecoil(gamePhaseState, 'over');
  }

  const reason = normalizeReason(payload.reason);
  if (reason) {
    setRecoil(endgameReasonState, reason);
  }

  const winnerRaw =
    payload.winner_id ??
    payload.winner_pid ??
    payload.winner_client_pid ??
    payload.winnerId;

  let winnerPid: string | undefined;
  if (typeof winnerRaw === 'string') {
    winnerPid = winnerRaw;
  } else if (winnerRaw != null) {
    winnerPid = String(winnerRaw);
  }
  if (!winnerRaw && payload.winner_seat != null) {
    winnerPid = seatToPid(Number(payload.winner_seat));
  }
  if (winnerPid) {
    setRecoil(winnerIdState, winnerPid);
  }

  const cells = (payload.winning_cells ?? payload.winningCombos) as
    | Array<{ col: number; row: number } | { x: number; y: number }>
    | undefined;

  if (cells && Array.isArray(cells)) {
    const combo = cells.map((keys: any) => {
      if (Array.isArray(keys)) {
        return [keys[0], keys[1]] as [number, number];
      }
      const c = keys.col ?? keys.x;
      const r = keys.row ?? keys.y;
      return [c, r] as [number, number];
    });
    setRecoil(winningComboState, combo);
  }

  const scores = payload.scores as
    | Array<{
        newScore: number;
        oldScore: number;
        userId: string | number;
      }>
    | undefined;

  if (scores && Array.isArray(scores)) {
    const id = getRecoil(userState.id);

    scores.forEach((score) => {
      const { newScore, oldScore, userId } = score;

      if (id === parseInt(String(userId), 10)) {
        const scoreChange = newScore - oldScore;
        setRecoil(userPlayerState.score, newScore);
        setRecoil(userState.score, newScore);
        setRecoil(endgameScoreChangeState, scoreChange);
      } else {
        setRecoil(opponentPlayerState.score, newScore);
      }
    });
  }

  void handlePhaseOver();
}

export function leaveGameChannel() {
  if (subscribedToken) {
    getEcho().leave(`game.${subscribedToken}`);
    subscribedToken = null;
  }
}

export function isSubscribedToGame(token: string): boolean {
  return subscribedToken === token;
}

export function subscribeToGame(
  token: string,
  options?: { onGameStarted?: () => void },
) {
  leaveGameChannel();
  subscribedToken = token;

  const echo = getEcho();
  const channel = echo.channel(`game.${token}`);

  channel.listen('.player.joined', (payload: Record<string, unknown>) => {
    syncPlayersFromPayload(payload);
  });

  channel.listen(
    '.game.started',
    (payload: Record<string, unknown>) => {
      hydrateFromSnapshot(payload);
      options?.onGameStarted?.();
    },
  );

  channel.listen('.move.played', (payload: Record<string, unknown>) => {
    applyMoveSnapshot(payload);
  });

  channel.listen('.game.finished', (payload: Record<string, unknown>) => {
    updatePlayersList(payload.players);
    syncPlayersFromPayload(payload);
    handleGameFinished(payload);
  });

  channel.listen('.rematch.restart', (payload: Record<string, unknown>) => {
    resetBoardRematch();
    closeModal();
    hydrateFromSnapshot(payload);
  });

  return channel;
}
